import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import MoreIcon from '@mui/icons-material/More';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { open } from '../drawer/drawerSlice';
import { Link, useNavigate } from 'react-router-dom';
import styles from "./Header.module.css";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel, Select, TextField } from '@mui/material';
import session from '../../app/Session';
import { actionLogout, setFrame, setSectionName, setUser } from '../../app/appSlice';
import { useTranslation } from 'react-i18next';
import { Circle } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { AlbumsService, TenantService } from '../../api/services';
import { getService, getApiError } from '../../app/api';
import { use } from 'i18next';
import { TenantDto, UserTenantDto, UserTenantItemDto } from '../../api/models';
import InfiniteScroll from "react-infinite-scroll-component";
import * as Media from '../media/mediaSlice'
import {setSelectedAlbumId, invalidate as invalidateAlbums} from "../album/ListAlbumSlice";

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '40ch',
        },
    },
}));

export default function Header() {
    const dispatch = useAppDispatch();
    let navigate = useNavigate();
    const { t, i18n } = useTranslation();
    let appState = useAppSelector(state=>state.app);
    let [showTenantDialog, setShowTenantDialog] = React.useState(false);
    let user = appState.user;

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
        React.useState<null | HTMLElement>(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const onUserChange = (args: any)=>{
        dispatch(setUser(session.user!))
    }

    React.useEffect( () => {
        let e = session.events.on('onChanged', onUserChange);
        return ()=> { 
            session.events.off('onChanged', onUserChange);
        }
    },[]);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(null);
        handleMobileMenuClose();

        switch(event.currentTarget.id){
            case 'logout':
                session.logout();
                dispatch(actionLogout());
                dispatch(setSectionName(""));
                navigate('/', { replace: true });
                break;
            case 'profile':
                dispatch(setSectionName('title.profile'));
                navigate('/profile', { replace: true });
                //dispatch(setFrame({id:'profile', open:true, viewFactory:()=> <Profile />}));                
                break;
            case 'tenant':
                setShowTenantDialog(true);
                break;
        }
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    let username = `${user?.name} ${user?.firstLastName} ${user?.secondLastName}`.trimEnd();

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >   
            <div style={{padding:'0 1rem 0.5rem 1rem'}}>{username}</div>
            <Divider/>
            <MenuItem id='profile' onClick={handleMenuClose}>
            <IconButton
                    size="small"
                    aria-label="account of current user"
                    aria-controls={menuId}
                    aria-haspopup="true"
                    color="inherit"
                >
                    <AccountCircle />
                    </IconButton>
                {t('profile')}
            </MenuItem>
            <MenuItem id='tenant' onClick={handleMenuClose}>
                <IconButton
                    size="small"
                    aria-label="account of current user"
                    aria-controls={menuId}
                    aria-haspopup="true"
                    color="inherit"
                >
                    <HomeIcon />
                </IconButton>
               {session.tenant?.name}
            </MenuItem>
            <Divider />            
            <MenuItem id='logout' onClick={handleMenuClose}>
            <IconButton
                    size="small"
                    aria-label="account of current user"
                    aria-controls={menuId}
                    aria-haspopup="true"
                    color="inherit"
                >
                    <LogoutIcon />
                    </IconButton>
                {t('logout')}
            </MenuItem>
        </Menu>
    );

    // const mobileMenuId = 'primary-search-account-menu-mobile';
    // const renderMobileMenu = (
    //     <Menu
    //         anchorEl={mobileMoreAnchorEl}
    //         anchorOrigin={{
    //             vertical: 'top',
    //             horizontal: 'right',
    //         }}
    //         id={mobileMenuId}
    //         keepMounted
    //         transformOrigin={{
    //             vertical: 'top',
    //             horizontal: 'right',
    //         }}
    //         open={isMobileMenuOpen}
    //         onClose={handleMobileMenuClose}
    //     >
    //         <div style={{padding:'0 1rem 0.5rem 1rem'}}>{username}</div>
    //         <Divider />
    //         <MenuItem id='profile' onClick={handleMenuClose}>
    //             <IconButton
    //                 size="large"
    //                 aria-label="account of current user"
    //                 aria-controls="primary-search-account-menu"
    //                 aria-haspopup="true"
    //                 color="inherit"
    //             >
    //                 <AccountCircle />
    //             </IconButton>
    //             <p>{t('profile')}</p>
    //         </MenuItem>
    //         <MenuItem id='logout' onClick={handleMenuClose}>
    //             <IconButton size="large" aria-label="show 4 new mails" color="inherit">                    
    //                     <LogoutIcon />                    
    //             </IconButton>
    //             <p>{t('logout')}</p>
    //         </MenuItem>
    //     </Menu>
    // );

    return (
        <>
            <AppBar position="static" className={styles.root}  >
                <Toolbar className={styles.toolbar} variant='dense' >
                  {user && (<IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        sx={{ mr: 2 }}
                        onClick={(e) => dispatch(open(true))}
                    >
                        <MenuIcon />
                    </IconButton>
                  )}
                    <div style={{display:'flex' ,alignItems:'center'}}>                        
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }} >
                        <Typography
                            className="App-logo"
                            variant="h6"
                            noWrap
                            component="div"                            
                            sx={{ display: { sm: 'block' } }}
                        > Memogram </Typography>                      
                    </Link>                    
                    {appState.sectionName && (
                        <div style={{display:'flex'}}>
                            <Typography style={{marginLeft: '0.5rem', marginRight:'0.5rem'}}>/</Typography>
                            <Typography >{t(appState.sectionName)}</Typography>
                           </div>
                        )} 
                    </div>                       
                    <Box sx={{ flexGrow: 1 }} >
                        {/* <Search>
                            <SearchIconWrapper>
                                <SearchIcon />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search…"
                                style={{ width: '100%' }}
                                inputProps={{ 'aria-label': 'search' }}
                            />
                        </Search> */}
                    </Box>
                   { user && (
                    <>
                    <Box >                
                        <IconButton
                            size="large"
                            edge="end"
                            aria-label="account of current user"
                            aria-controls={menuId}
                            aria-haspopup="true"
                            onClick={handleProfileMenuOpen}
                            color="inherit"
                        >
                            <AccountCircle />
                        </IconButton>
                    </Box>
                    {/* <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="show more"
                            aria-controls={mobileMenuId}
                            aria-haspopup="true"
                            onClick={handleMobileMenuOpen}
                            color="inherit"
                        >
                            <MoreIcon />
                        </IconButton>
                    </Box> */}
                    </>
                   )} 
                </Toolbar>
            </AppBar>
            {/* {renderMobileMenu} */}
            {renderMenu}
            {showTenantDialog && <TenantDialog onClose={() => setShowTenantDialog(false)} /> }
        </>
    );
}


interface TenantDialogData {
    items: UserTenantItemDto[];
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
}

interface TenantState{
    id: string;
    role:string;
}

function TenantDialog({  onClose }: { onClose: () => void }) {
    const { t } = useTranslation();        
    const { enqueueSnackbar } = useSnackbar();
    const [tenant, setTenant] = React.useState<TenantState|null>((session.tenant && session.user && {id: session.tenant.id!, role: session.user.role! }) || null);    
    const [invalidated, setInvalidated] = React.useState(true);
    const [state, setState] = React.useState<TenantDialogData>({
            items: [],
            page: 1,
            pageSize: 25,
            totalPages: 0,
            totalItems: 0,
    });
    const dispatch = useAppDispatch();
    let navigate = useNavigate();

    React.useEffect(() => {
        if (invalidated) {
            let srv = getService(TenantService);
            srv.getUserTenants({ page: state.page, pageSize: state.pageSize })
                .then(res => {
                    setState((prev) => (
                        {
                            ...prev,
                            items: res.items || [],
                            page: res.page,
                            totalItems: res.totalItems,
                            totalPages: res.totalPages
                        }));
                    setInvalidated(false);

                });
        }
    }, [invalidated]);

    async function fetchMore() {
        if (state.page <= state.totalPages) {
            let srv = getService(TenantService);
            let res = await srv.getUserTenants({ page: state.page + 1, pageSize: state.pageSize });

            setState((prev) => (
                {
                    ...prev,
                    items: [...prev.items, ...res.items || []],
                    page: res.page,
                    totalItems: res.totalItems,
                    totalPages: res.totalPages
                }));
            }
    }
 
    async function onTenantSelected() {
        try {
            let srv = getService(TenantService);
            await srv.setSessionTenant({ id: tenant?.id});                                   
        }catch (error:any) {
            enqueueSnackbar(t('Error changing Account: {{error}}', { error:getApiError(error) }), { variant: 'error' });   
            throw error;
        }       

        enqueueSnackbar(t('Account changed successfully!'), { variant: 'success' });                    
       
        try{
            await session.reloadSession();
        }
        catch (error:any) {
            enqueueSnackbar(t('Error loading session: {{error}}', { error:getApiError(error) }), { variant: 'error' });   
            throw error;
        }

        dispatch(invalidateAlbums(true));            
        dispatch(Media.setAlbumId({albumId: null})); 
        dispatch(setSectionName('title.galery'));
        onClose();        
        window.location.assign("/");                
    }

    const formatName = (name:string)=>{
        if(name.startsWith('tenant-')){
            return name.substring(7);
        }
        return name;
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>{t('Select Account')}</DialogTitle>
            <DialogContent>
                <div style={{ position: 'relative' }}>
                    {invalidated === true && (
                        <div className="progress-indicator">
                            <CircularProgress size="5rem" />
                        </div>
                    )}
                    <div className={invalidated === true ? 'content-loading' : undefined}>
                        <InfiniteScroll
                            className={styles.image_grid}
                            dataLength={state.items.length}
                            next={fetchMore}
                            hasMore={state.page < state.totalPages}                            
                            loader={<CircularProgress size="5rem" />}
                        >
                            {state.items.map(item => (
                                <div key={item.id} className={styles.tenantCard}>
                                    <div>
                                        <input type='radio' onChange={()=>setTenant({id:item.id!, role:item.role!})} checked={item.id == tenant?.id} />
                                    </div>
                                    <div>
                                        <label>{item.name && formatName(item.name)}</label> <br/>
                                        <label>{item.role && t(item.role)}</label>
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>

                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('Close')}</Button>
                <LoadingButton
                    variant="text"
                    loading={invalidated}
                    loadingPosition="start"
                    color="primary"
                    onClick={onTenantSelected}>
                    {t('Accept')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
